/**
 * Real refraction for the Liquid Glass theme.
 *
 * Every other theme paints its glass with `backdrop-filter: blur()`, which only
 * softens the pixels behind a panel. This module adds the missing half: SVG
 * displacement maps that *bend* those pixels, so a pane's rim distorts whatever
 * it is sitting over the way a thick sheet of glass does.
 *
 * Why this is JavaScript and not four lines of CSS
 * ------------------------------------------------
 * A filter used through `backdrop-filter` gets no automatic sizing from
 * Chromium — relative units (`filterUnits="objectBoundingBox"`, a `width="100%"`
 * primitive subregion) are simply not resolved against the element the way they
 * are for a normal `filter`. The displacement map has to be built in absolute
 * pixels, matching the box it will bend, which means one filter per element
 * size and something that watches for those sizes changing.
 *
 * Filters are therefore cached by quantised size, not minted per element. A
 * board's cards are near enough identical, so a few dozen panes share a handful
 * of filters, and the cache is capped so a pathological layout degrades to the
 * frosted look instead of flooding the document with `<filter>` nodes.
 *
 * Everything here is inserted when the theme activates and torn out when it
 * deactivates, so no other theme carries a node, an observer, or an inline
 * style it did not ask for.
 */

/** A displacement channel at 128 moves its sample by nothing. */
const NEUTRAL = 'rgb(128,128,128)';

const HOST_ID = 'pk-liquid-glass-filters';

/** Sizes are rounded to this many px before becoming a cache key. */
const SIZE_STEP = 8;

/**
 * Beyond this many distinct sizes the theme stops refracting and leaves the
 * frost in place. Reached only by a layout with dozens of genuinely different
 * pane sizes on screen at once.
 */
const MAX_FILTERS = 64;

interface Lens {
  /** Thickness of the bevel in px — a real pane's edge does not scale with it. */
  bevel: number;
  /** Peak displacement at the very edge, in px. */
  scale: number;
  /** Blur applied *after* the displacement; small, or it erases the bend. */
  blur: number;
}

/** Which surfaces get glass, most specific first. */
const SURFACES: Array<{ selector: string; lens: Lens }> = [
  { selector: '.pk-input', lens: { bevel: 10, scale: 9, blur: 8 } },
  { selector: '.pk-card', lens: { bevel: 16, scale: 20, blur: 2 } },
  { selector: '.pk-panel', lens: { bevel: 24, scale: 34, blur: 3 } },
];

const SELECTOR = SURFACES.map((surface) => surface.selector).join(',');

/** Cache key → filter id. */
const filters = new Map<string, string>();

/** Every element currently carrying an inline `backdrop-filter` from us. */
const glassed = new Set<HTMLElement>();

let host: SVGSVGElement | null = null;
let defs: SVGDefsElement | null = null;
let resizeObserver: ResizeObserver | null = null;
let mutationObserver: MutationObserver | null = null;
let frame = 0;

/**
 * One axis of the bevel, as a data-URI image whose R (or G) channel encodes
 * displacement.
 *
 * Both edges bend *inwards* — the leading edge samples to the right/down, the
 * trailing edge to the left/up. That is deliberate: the filter region is the
 * element's own box, so an outward bend would sample past it and fringe every
 * pane in transparent black. Bending inwards compresses the pane's own backdrop
 * against the rim, which is the effect we were after anyway.
 *
 * The unused channels sit at 128 so the *other* pass can point its channel
 * selector at one of them and get a guaranteed zero.
 */
function displacementMap(axis: 'x' | 'y', width: number, height: number, bevel: number): string {
  const [lead, trail] =
    axis === 'x' ? ['rgb(255,128,128)', 'rgb(0,128,128)'] : ['rgb(128,255,128)', 'rgb(128,0,128)'];
  const [x2, y2] = axis === 'x' ? [1, 0] : [0, 1];

  // A bevel wider than half the side would leave no flat centre to read through.
  const side = axis === 'x' ? width : height;
  const stop = Math.min(bevel / Math.max(side, 1), 0.45);

  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}">` +
    `<linearGradient id="g" x1="0" y1="0" x2="${x2}" y2="${y2}">` +
    `<stop offset="0" stop-color="${lead}"/>` +
    `<stop offset="${Number(stop.toFixed(4))}" stop-color="${NEUTRAL}"/>` +
    `<stop offset="${Number((1 - stop).toFixed(4))}" stop-color="${NEUTRAL}"/>` +
    `<stop offset="1" stop-color="${trail}"/>` +
    `</linearGradient>` +
    `<rect width="${width}" height="${height}" fill="url(#g)"/>` +
    `</svg>`;

  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

/**
 * Two chained displacements rather than one.
 *
 * `feDisplacementMap` reads both axes from a single map, and a map carrying an
 * x ramp *and* a y ramp cannot express the corners — the two ramps overwrite
 * each other there. Running the axes as separate passes, each pointing its idle
 * selector at the flat blue channel, keeps the corners bending diagonally.
 *
 * Every length is in user-space px and the filter region is pinned to the
 * element's box, because that is the one thing Chromium will not work out for
 * itself here. `color-interpolation-filters="sRGB"` is not optional either: the
 * default converts to linear RGB first, which moves the 128 neutral and leaves
 * the flat centre drifting.
 */
function mintFilter(width: number, height: number, lens: Lens): string | null {
  const key = `${width}x${height}x${lens.bevel}x${lens.scale}`;
  const cached = filters.get(key);
  if (cached) return cached;
  if (!defs || filters.size >= MAX_FILTERS) return null;

  const id = `pk-lens-${filters.size}`;
  const image = (axis: 'x' | 'y', result: string): string => {
    const href = displacementMap(axis, width, height, lens.bevel);
    // `href` is SVG2; `xlink:href` is what older WebKit builds still read.
    return (
      `<feImage href="${href}" xlink:href="${href}" result="${result}"` +
      ` x="0" y="0" width="${width}" height="${height}" preserveAspectRatio="none"/>`
    );
  };

  const markup =
    `<filter id="${id}" color-interpolation-filters="sRGB" filterUnits="userSpaceOnUse"` +
    ` x="0" y="0" width="${width}" height="${height}">` +
    image('x', 'mapX') +
    image('y', 'mapY') +
    `<feDisplacementMap in="SourceGraphic" in2="mapX" scale="${lens.scale}"` +
    ` xChannelSelector="R" yChannelSelector="B" result="bentX"/>` +
    `<feDisplacementMap in="bentX" in2="mapY" scale="${lens.scale}"` +
    ` xChannelSelector="B" yChannelSelector="G"/>` +
    `</filter>`;

  defs.insertAdjacentHTML('beforeend', markup);
  filters.set(key, id);
  return id;
}

function lensFor(element: Element): Lens | null {
  return SURFACES.find((surface) => element.matches(surface.selector))?.lens ?? null;
}

/** Sizes the element's lens and hangs it off the element's own style attribute. */
function refract(element: HTMLElement): void {
  const lens = lensFor(element);
  if (!lens) return;

  const { width, height } = element.getBoundingClientRect();
  // A collapsed or not-yet-laid-out pane has nothing to bend.
  if (width < 2 * SIZE_STEP || height < 2 * SIZE_STEP) return;

  /*
   * Always round *up*. The filter region is pinned to these numbers, and a
   * region even a pixel short of the element leaves a strip along the edge that
   * no filter touches — a visible seam. Rounding up overshoots instead, which
   * `backdrop-filter` clips away harmlessly; the only cost is that the trailing
   * bevel ramp finishes just past the rim, so the bottom and right edges bend a
   * touch more softly than the top and left.
   */
  const quantised = (value: number): number => Math.max(SIZE_STEP, Math.ceil(value / SIZE_STEP) * SIZE_STEP);
  const id = mintFilter(quantised(width), quantised(height), lens);
  // Over the cache cap the stylesheet's frosted rule is left to stand.
  if (!id) return;

  const value = `url(#${id}) blur(${lens.blur}px) saturate(170%) brightness(1.06)`;
  element.style.setProperty('backdrop-filter', value);
  element.style.setProperty('-webkit-backdrop-filter', value);
  glassed.add(element);
}

/** Coalesces a burst of resizes and insertions into one pass. */
function schedule(elements: Iterable<HTMLElement>): void {
  const pending = [...elements];
  if (!pending.length || frame) return;
  frame = requestAnimationFrame(() => {
    frame = 0;
    for (const element of pending) refract(element);
  });
}

function track(root: ParentNode): void {
  const elements = [
    ...(root instanceof Element && root.matches(SELECTOR) ? [root as HTMLElement] : []),
    ...(root.querySelectorAll?.(SELECTOR) ?? []),
  ] as HTMLElement[];

  for (const element of elements) {
    refract(element);
    resizeObserver?.observe(element);
  }
}

/**
 * Whether this engine renders an SVG filter referenced from `backdrop-filter`.
 *
 * Only Blink does — it is a Chromium extension that never made it into the CSS
 * spec. WebKit and Gecko *parse* `url(#x)` there quite happily and then drop the
 * whole declaration, which would cost the frosted fallback as well as the
 * refraction, so `CSS.supports()` is the wrong question to ask: it answers "does
 * this parse", not "does this paint". `navigator.userAgentData` is a Blink-only
 * API, and it is present in the WebView2 and Android WebView that the desktop
 * and mobile builds run on.
 */
export function supportsRefraction(): boolean {
  if (typeof window === 'undefined' || typeof CSS === 'undefined') return false;
  if (!CSS.supports('backdrop-filter', 'blur(1px)')) return false;
  // Refraction is exactly what this setting asks us not to do.
  if (window.matchMedia?.('(prefers-reduced-transparency: reduce)').matches) return false;
  return 'userAgentData' in navigator;
}

/** Builds the lenses and starts following the panes that wear them. */
export function enableLiquidGlass(): void {
  if (!supportsRefraction() || !document.body || host) return;

  host = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  host.setAttribute('id', HOST_ID);
  host.setAttribute('aria-hidden', 'true');
  // Collapsed rather than `display: none` — Blink skips filters defined in a
  // subtree that is not rendered.
  host.setAttribute('style', 'position:absolute;width:0;height:0;overflow:hidden;pointer-events:none');
  host.innerHTML = '<defs></defs>';
  document.body.appendChild(host);
  defs = host.querySelector('defs');

  // A pane that changes size needs a lens cut to the new one.
  resizeObserver = new ResizeObserver((entries) => {
    schedule(entries.map((entry) => entry.target as HTMLElement));
  });

  // Routes, modals and board columns all arrive after the theme does.
  mutationObserver = new MutationObserver((records) => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (node.nodeType === Node.ELEMENT_NODE) track(node as Element);
      }
    }
  });
  mutationObserver.observe(document.body, { childList: true, subtree: true });

  track(document.body);
  document.documentElement.setAttribute('data-liquid-refraction', 'on');
}

/** Restores the DOM and the stylesheet's own rules for every other theme. */
export function disableLiquidGlass(): void {
  document.documentElement.removeAttribute('data-liquid-refraction');

  if (frame) cancelAnimationFrame(frame);
  frame = 0;
  resizeObserver?.disconnect();
  mutationObserver?.disconnect();
  resizeObserver = null;
  mutationObserver = null;

  for (const element of glassed) {
    element.style.removeProperty('backdrop-filter');
    element.style.removeProperty('-webkit-backdrop-filter');
  }
  glassed.clear();
  filters.clear();

  host?.remove();
  host = null;
  defs = null;
}
